import { useState } from 'react';
import { Button, Close, Modal, Select } from '@react-apps/design-system';
import { useContentItem } from '../hooks/use-content-items';
import { contentDisplayName } from '../display';
import { IconButton } from '@/shared/components/icon-button';
import { ExamPickerModal } from './ExamPickerModal';

/** Como os filtros vivem na URL (`format`, `annulled`, `examId`): string vazia é "sem filtro". */
export interface QuestionFilterValues {
  format: '' | 'MULTIPLE_CHOICE' | 'ESSAY';
  annulled: '' | 'true' | 'false';
  examId: string;
}

export const EMPTY_QUESTION_FILTERS: QuestionFilterValues = {
  format: '',
  annulled: '',
  examId: '',
};

export function countQuestionFilters(values: QuestionFilterValues): number {
  return [values.format, values.annulled, values.examId].filter(Boolean).length;
}

const FORMAT_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'MULTIPLE_CHOICE', label: 'Múltipla escolha' },
  { value: 'ESSAY', label: 'Discursiva' },
];

/**
 * O mesmo botão "Filtros" das listagens do TOTVS e de vendas: o `secondary` do
 * DS é cinza-claro fixo e vira um bloco branco no tema escuro.
 */
const OUTLINE_BUTTON =
  'border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition';

const ANNULLED_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'true', label: 'Só anuladas' },
  { value: 'false', label: 'Só não anuladas' },
];

/**
 * Filtros de questão da listagem de conteúdos: um botão que abre um modal, no
 * padrão do `TotvsSyncTasksFiltersModal` (rascunho local, Limpar / Cancelar /
 * Aplicar).
 *
 * O picker de prova **substitui** este modal em vez de empilhar: o `Modal` do
 * DS registra o Escape em `document` por instância, então um Escape fecharia
 * os dois, e o primeiro a fechar destravaria o scroll do body. O rascunho
 * vive aqui, fora dos dois modais, e sobrevive à ida e volta.
 */
export function QuestionFilters({
  value,
  onApply,
}: {
  value: QuestionFilterValues;
  onApply: (next: QuestionFilterValues) => void;
}) {
  const [mode, setMode] = useState<'closed' | 'filters' | 'picker'>('closed');
  const [draft, setDraft] = useState(value);
  const count = countQuestionFilters(value);

  const open = () => {
    setDraft(value);
    setMode('filters');
  };
  const update = (next: Partial<QuestionFilterValues>) =>
    setDraft((prev) => ({ ...prev, ...next }));

  return (
    <>
      <button
        type="button"
        className={OUTLINE_BUTTON}
        aria-haspopup="dialog"
        aria-label={
          count > 0
            ? `Filtros de questão, ${count} ${count === 1 ? 'aplicado' : 'aplicados'}`
            : 'Filtros de questão'
        }
        onClick={open}
      >
        Filtros de questão{count > 0 ? ` (${count})` : ''}
      </button>

      <Modal
        open={mode === 'filters'}
        onClose={() => setMode('closed')}
        title="Filtrar questões"
        size="lg"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDraft(EMPTY_QUESTION_FILTERS)}
            >
              Limpar
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setMode('closed')}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                onApply(draft);
                setMode('closed');
              }}
            >
              Aplicar
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Formato"
              value={draft.format}
              onChange={(e) =>
                update({
                  format: e.target.value as QuestionFilterValues['format'],
                })
              }
              options={FORMAT_OPTIONS}
            />
            <Select
              label="Anulada"
              value={draft.annulled}
              onChange={(e) =>
                update({
                  annulled: e.target.value as QuestionFilterValues['annulled'],
                })
              }
              options={ANNULLED_OPTIONS}
            />
          </div>
          <div className="space-y-1">
            <p className="text-(--color-text-primary) text-sm font-medium">
              Prova
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={OUTLINE_BUTTON}
                onClick={() => setMode('picker')}
              >
                {draft.examId ? 'Trocar prova' : 'Escolher prova'}
              </button>
              {draft.examId && (
                <>
                  <ExamName examId={draft.examId} />
                  <IconButton
                    label="Limpar prova"
                    icon={<Close size="sm" />}
                    onClick={() => update({ examId: '' })}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Montado só aberto: fechado, ele buscaria 100 provas assim que o Tipo
          vira Questão, sem ninguém pedir. */}
      {mode === 'picker' && (
        <ExamPickerModal
          open
          onClose={() => setMode('filters')}
          onPick={(exam) => {
            update({ examId: exam.id });
            setMode('filters');
          }}
          title="Questões de qual prova?"
          description="Clique na prova. A listagem mostra só as questões dela."
          emptyMessage="Nenhuma prova real encontrada."
        />
      )}
    </>
  );
}

/**
 * Rótulo da prova pelo detalhe, que traz o `label` pronto. É o mesmo caminho
 * para a prova recém-escolhida e para a que veio na URL (link colado, reload).
 * Id que não resolve mostra o próprio id, para o admin ver o que limpar.
 */
function ExamName({ examId }: { examId: string }) {
  const { data, isLoading } = useContentItem(examId);
  const text = isLoading
    ? 'carregando…'
    : data
      ? contentDisplayName({
          type: data.item.type,
          id: data.item.id,
          label: data.label,
          examKind:
            (data.item.body as { examKind?: 'REAL_EXAM' | 'MOCK_EXAM' })
              .examKind ?? null,
        })
      : examId;
  return (
    <span className="text-(--color-text-primary) min-w-0 truncate text-sm">
      {text}
    </span>
  );
}
